import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EllipsisVerticalIcon,
  FunnelIcon,
  XMarkIcon,
  EyeIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

import MainLayout from '../components/Layout/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import useDebounce from '../hooks/useDebounce';
import { 
  Input, 
  Select, 
  Button, 
  Modal, 
  DataTable, 
  showToast,
  ConfirmDialog,
  DateRangePicker
} from '../components/UI';
import { logSheetService } from '../services/logSheetService';
import { LogSheet, LogSheetFilters, LogSheetFormData } from '../types/logSheetTypes';
import enquiryService, { Enquiry } from '../services/enquiryService';
import { userService } from '../services/userService';
import { boreSizeService } from '../services/boreSizeService';
import { BoreSize } from '../types/boreSize';
import { rigService } from '../services/rigService';
import itemService from '../services/itemService';
import { Item } from '../types/item';

interface TableParams {
  page: number;
  limit: number;
  search: string;
  enquiry_id?: number;
  user_id?: number;
  rig_id?: number;
}


// Validation schema - Required fields: Enquiry, Log Sheet No, Rig, Log Date, Service Date, Bore Size, No of Feet
const logSheetSchema: yup.ObjectSchema<LogSheetFormData> = yup.object().shape({
  enquiry_id: yup.number().required('Enquiry is required'),
  log_sheet_no: yup.string().required('Log Sheet No is required').matches(/^\d{4,6}$/, 'Log Sheet No must be 4 to 6 digits'),
  rig_id: yup.number().required('Rig is required').min(1, 'Please select a rig'),
  rig_no: yup.string().required('RIG number is required'),
  log_date: yup.string().required('Log date is required'),
  service_date: yup.string().required('Service date is required'),
  bore_size_id: yup.number().required('Bore size is required'),
  no_of_feet: yup.number().min(0.01, 'Number of feet must be greater than 0').required('Number of feet is required'),
  pvc_1_item_id: yup.number().transform((value, originalValue) => originalValue === '' || isNaN(value) ? undefined : value).optional(),
  pvc_1_feet: yup.number().transform((value, originalValue) => originalValue === '' || isNaN(value) ? undefined : value).min(0, 'PVC 1 feet must be at least 0').optional(),
  pvc_2_item_id: yup.number().transform((value, originalValue) => originalValue === '' || isNaN(value) ? undefined : value).optional(),
  pvc_2_feet: yup.number().transform((value, originalValue) => originalValue === '' || isNaN(value) ? undefined : value).min(0, 'PVC 2 feet must be at least 0').optional(),
  pipeline_item_id: yup.number().transform((value, originalValue) => originalValue === '' || isNaN(value) ? undefined : value).optional(),
  pipeline_feet: yup.number().transform((value, originalValue) => originalValue === '' || isNaN(value) ? undefined : value).min(0, 'Pipeline feet must be at least 0').optional(),
  bore_well_yield: yup.mixed<string | number>().transform((value, originalValue) => originalValue === '' ? undefined : value).optional(),
  from_rpm: yup.number()
    .transform((value, originalValue) => originalValue === '' || isNaN(value) ? undefined : value)
    .min(0, 'From RPM must be at least 0')
    .optional()
    .test('rpm-required-together', 'Both From RPM and To RPM must be provided', function(value) {
      const { to_rpm } = this.parent;
      // If one is filled, both must be filled
      if ((value !== undefined && to_rpm === undefined) || (value === undefined && to_rpm !== undefined)) {
        return false;
      }
      return true;
    }),
  to_rpm: yup.number()
    .transform((value, originalValue) => originalValue === '' || isNaN(value) ? undefined : value)
    .min(0, 'To RPM must be at least 0')
    .optional()
    .test('rpm-required-together', 'Both From RPM and To RPM must be provided', function(value) {
      const { from_rpm } = this.parent;
      // If one is filled, both must be filled
      if ((value !== undefined && from_rpm === undefined) || (value === undefined && from_rpm !== undefined)) {
        return false;
      }
      return true;
    })
    .test('to-rpm-greater', 'To RPM must be greater than From RPM', function(value) {
      const { from_rpm } = this.parent;
      // Only validate if both values are present
      if (value !== undefined && from_rpm !== undefined) {
        return value > from_rpm;
      }
      return true;
    }),
  from_diesel: yup.number().transform((value, originalValue) => originalValue === '' || isNaN(value) ? undefined : value).min(0, 'From diesel must be at least 0').optional(),
  to_diesel: yup.number().transform((value, originalValue) => originalValue === '' || isNaN(value) ? undefined : value).min(0, 'To diesel must be at least 0').optional(),
  diesel_filled_for_lorry: yup.number().transform((value, originalValue) => originalValue === '' || isNaN(value) ? undefined : value).min(0, 'Diesel filled for lorry must be at least 0').optional(),
  user_id: yup.number().transform((value, originalValue) => originalValue === '' || isNaN(value) ? undefined : value).optional(),
  prev_hammer_run: yup.number().transform((value, originalValue) => originalValue === '' || isNaN(value) ? undefined : value).min(0, 'Prev hammer run must be at least 0').optional(),
  hammer_run: yup.number().transform((value, originalValue) => originalValue === '' || isNaN(value) ? undefined : value).min(0, 'Hammer run must be at least 0').optional(),
  bit_no: yup.string().max(50, 'Bit No must be less than 50 characters').optional(),
  bit_depth: yup.number().transform((value, originalValue) => originalValue === '' || isNaN(value) ? undefined : value).min(0, 'Bit Depth must be positive').optional(),
  remarks: yup.string().optional()
});


const LogSheetManagement: React.FC = () => {
  const { user } = useAuth();
  const [logSheets, setLogSheets] = useState<LogSheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLogSheet, setEditingLogSheet] = useState<LogSheet | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; logSheet: LogSheet | null }>({
    show: false,
    logSheet: null
  });
  const [deleting, setDeleting] = useState(false);
  const [viewingLogSheet, setViewingLogSheet] = useState<LogSheet | null>(null);

  // Form-specific states
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [fieldTeamUsers, setFieldTeamUsers] = useState<any[]>([]);
  const [boreSizes, setBoreSizes] = useState<BoreSize[]>([]);
  const [selectedEnquiry, setSelectedEnquiry] = useState<Enquiry | null>(null);

  // Default date range: last one month
  const getDefaultDateRange = (): [Date, Date] => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 1);
    return [startDate, endDate];
  };

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(getDefaultDateRange());
  const [rigs, setRigs] = useState<Array<{id: number, rig_no: string}>>([]);
  const [pvcItems, setPvcItems] = useState<Item[]>([]);
  
  // Pagination and filter states
  const [tableParams, setTableParams] = useState<TableParams>({
    page: 1,
    limit: 10,
    search: '',
    enquiry_id: undefined,
    user_id: undefined,
    rig_id: undefined
  });

  const [sorting, setSorting] = useState<SortingState>([
    { id: 'log_date', desc: true }
  ]);

  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const requestIdRef = useRef(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState<number | null>(null);

  // Debounced search
  const debouncedSearch = useDebounce(tableParams.search, 500);

  // Form setup
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    setFocus,
    formState: { errors }
  } = useForm<LogSheetFormData>({
    resolver: yupResolver(logSheetSchema),
    shouldFocusError: false,
    criteriaMode: 'firstError',
    mode: 'onSubmit',
    defaultValues: {
      enquiry_id: 0,
      log_sheet_no: '',
      rig_id: 0,
      rig_no: '',
      log_date: new Date().toISOString().split('T')[0],
      service_date: new Date().toISOString().split('T')[0],
      pvc_1_item_id: 0,
      pvc_1_feet: 0,
      pvc_2_item_id: undefined,
      pvc_2_feet: undefined,
      bore_size_id: 0,
      no_of_feet: 0,
      bit_no: '',
      bit_depth: 0
    }
  });

  // Auto-calculate hammer_run from prev_hammer_run
  // When editing: prev_hammer_run is calculated once on load, then normal calculation applies
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      // Always use forward calculation: hammer_run = prev_hammer_run + no_of_feet - pvc_1_feet
      // This applies to both create and edit modes (after initial load)
      if (!name || name === 'prev_hammer_run' || name === 'no_of_feet' || name === 'pvc_1_feet') {
        const prevHammerRun = value.prev_hammer_run || 0;
        const noOfFeet = value.no_of_feet || 0;
        const pvc1Feet = value.pvc_1_feet || 0;
        const calculatedHammerRun = Number((prevHammerRun + noOfFeet - pvc1Feet).toFixed(2));

        // Only update if the calculated value is different from current value
        if (value.hammer_run !== calculatedHammerRun) {
          setValue('hammer_run', calculatedHammerRun, { shouldValidate: false, shouldDirty: false });
        }
      }
    });
    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load lists for dropdowns
  const loadLists = useCallback(async () => {
    try {
      const promises: Promise<any>[] = [];

      // Load enquiries for admin/managers, or user's assigned enquiries for field team
      if (user?.role === 'ADMIN') {
        promises.push(enquiryService.getEnquiries({ limit: 1000 }));
        promises.push(userService.getUsers({ limit: 100 }));
        promises.push(boreSizeService.getBoreSizes({ limit: 1000 }));
        promises.push(itemService.getItems({ limit: 1000 })); // Load all items
      } else if (user?.role === 'MANAGER') {
        // Managers only need enquiries - they can't create/edit log sheets
        promises.push(enquiryService.getEnquiries({ limit: 1000 })); // Backend will filter by user_id
      } else if (user?.role === 'FIELD_TEAM') {
        promises.push(enquiryService.getEnquiries({ limit: 1000 })); // Will be filtered by backend
        promises.push(boreSizeService.getBoreSizes({ limit: 1000 }));
        promises.push(itemService.getItems({ limit: 1000 })); // Load all items
      }

      const responses = await Promise.all(promises);

      if (user?.role === 'ADMIN') {
        const [enquiriesRes, usersRes, boreSizesRes, itemsRes] = responses;
        setEnquiries(enquiriesRes.enquiries || []);

        // Filter field team users
        const allUsers = usersRes.data || [];
        setFieldTeamUsers(allUsers.filter((u: any) => u.role === 'FIELD_TEAM'));

        // Set bore sizes
        setBoreSizes(boreSizesRes.data || []);

        // Set PVC and Pipeline items
        const allItems = itemsRes.data || [];
        setPvcItems(allItems.filter((item: Item) => item.type === 'PVC' || item.type === 'PIPELINE'));
      } else if (user?.role === 'MANAGER') {
        const [enquiriesRes] = responses;
        setEnquiries(enquiriesRes.enquiries || []);
      } else {
        const [enquiriesRes, boreSizesRes, itemsRes] = responses;
        setEnquiries(enquiriesRes.enquiries || []);
        setBoreSizes(boreSizesRes.data || []);

        // Set PVC and Pipeline items
        const allItems = itemsRes.data || [];
        setPvcItems(allItems.filter((item: Item) => item.type === 'PVC' || item.type === 'PIPELINE'));
      }
    } catch (error) {
      console.error('Error loading lists:', error);
      showToast.error('Failed to load dropdown options. Please refresh the page.');
    }
  }, [user?.role]);

  // Load log sheets
  const loadLogSheets = useCallback(async (params: TableParams, sortingState: SortingState) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);

    try {
      const filters: LogSheetFilters = {
        page: params.page,
        limit: params.limit,
        search: params.search || undefined,
        enquiry_id: params.enquiry_id,
        user_id: params.user_id,
        rig_id: params.rig_id,
        fromDate: dateRange[0]?.toISOString().split('T')[0],
        toDate: dateRange[1]?.toISOString().split('T')[0],
        sortBy: sortingState[0]?.id as any || 'log_date',
        sortOrder: sortingState[0]?.desc ? 'desc' : 'asc'
      };

      const response = await logSheetService.getLogSheets(filters);

      if (requestId !== requestIdRef.current) return;
      setLogSheets(response.logSheets);
      setTotalPages(response.pagination.pages);
      setTotalRecords(response.pagination.total);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error('Error loading log sheets:', error);
      showToast.error('Failed to load log sheets');
      setLogSheets([]);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [dateRange]);

  // Load rigs
  const loadRigs = useCallback(async () => {
    try {
      const response = await rigService.getRigs({ page: 1, limit: 1000 });
      setRigs(response.data);
    } catch (error) {
      console.error('Error loading rigs:', error);
    }
  }, []);

  // Effects
  useEffect(() => {
    if (user) {
      loadLists();
      loadRigs(); // Load rigs for filter dropdown
    }
  }, [user, loadLists, loadRigs]);

  useEffect(() => {
    if (user) {
      loadLogSheets(tableParams, sorting);
    }
  }, [user, debouncedSearch, tableParams, tableParams.page, tableParams.limit, tableParams.enquiry_id, tableParams.user_id, tableParams.rig_id, dateRange, sorting, loadLogSheets]);

  useEffect(() => {
    setTableParams(prev => ({ ...prev, search: debouncedSearch, page: 1 }));
  }, [debouncedSearch]);

  // Effect for date range changes
  useEffect(() => {
    if (dateRange[0] || dateRange[1]) {
      setTableParams(prev => ({ ...prev, page: 1 }));
    }
  }, [dateRange]);

  // Effect for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Also load rigs when modal opens (in case they weren't loaded yet)
  useEffect(() => {
    if (showModal) {
      loadRigs();
    }
  }, [showModal, loadRigs]);


  // Async load function for enquiries with pagination (for CREATE form - unmapped only)
  const loadEnquiryOptions = useCallback(async (search: string, page: number) => {
    try {
      const response = await enquiryService.getEnquiries({
        page: page || 1,
        limit: 50,
        search: search || undefined,
        unmapped: true, // Only get enquiries without log sheets
        without_invoice: 'true' // Only get enquiries without invoices
      });

      const options = response.enquiries.map(enquiry => ({
        value: enquiry.id.toString(),
        label: `${enquiry.enquiry_no || 'N/A'} - ${enquiry.contact_person || 'N/A'} (${enquiry.contact_no || 'N/A'})`,
        enquiry: enquiry // Store the full enquiry object for reference
      }));

      return {
        options,
        hasMore: page < response.pagination.pages
      };
    } catch (error) {
      console.error('Error loading enquiries:', error);
      return {
        options: [],
        hasMore: false
      };
    }
  }, []);

  // Async load function for filter dropdown (only enquiries WITH log sheets)
  const loadFilterEnquiryOptions = useCallback(async (search: string, page: number) => {
    try {
      // Get unique enquiry IDs from existing log sheets
      const enquiryIdsWithLogSheets = new Set(logSheets.map(ls => ls.enquiry_id));

      // Filter enquiries to only show those with log sheets
      const filteredEnquiries = enquiries.filter(enquiry => {
        const hasLogSheet = enquiryIdsWithLogSheets.has(enquiry.id);
        const matchesSearch = !search ||
          enquiry.enquiry_no?.toLowerCase().includes(search.toLowerCase()) ||
          enquiry.contact_person?.toLowerCase().includes(search.toLowerCase()) ||
          enquiry.contact_no?.includes(search);
        return hasLogSheet && matchesSearch;
      });

      const options = filteredEnquiries.map(enquiry => ({
        value: enquiry.id.toString(),
        label: `${enquiry.enquiry_no || 'N/A'} - ${enquiry.contact_person || 'N/A'} (${enquiry.contact_no || 'N/A'})`
      }));

      return {
        options,
        hasMore: false // Using static list, no pagination needed
      };
    } catch (error) {
      console.error('Error loading filter enquiries:', error);
      return {
        options: [],
        hasMore: false
      };
    }
  }, [enquiries, logSheets]);


  // Memoized field team options
  const fieldTeamOptions = useMemo(() => {
    return fieldTeamUsers.map(user => ({
      value: user.id.toString(),
      label: `${user.name} (${user.mobile})`
    }));
  }, [fieldTeamUsers]);

  // Memoized bore size options
  const boreSizeOptions = useMemo(() => {
    return boreSizes.map(boreSize => ({
      value: boreSize.id.toString(),
      label: boreSize.size
    }));
  }, [boreSizes]);

  // Event handlers
  const handleCreate = () => {
    setEditingLogSheet(null);
    setSelectedEnquiry(null);
    reset({
      enquiry_id: 0,
      log_sheet_no: '',
      rig_id: 0,
      rig_no: '',
      log_date: new Date().toISOString().split('T')[0],
      service_date: new Date().toISOString().split('T')[0],
      pvc_1_item_id: 0,
      pvc_1_feet: 0,
      pvc_2_item_id: undefined,
      pvc_2_feet: undefined,
      bore_size_id: 0,
      no_of_feet: 0,
      bit_no: '',
      bit_depth: 0,
      user_id: user?.role === 'FIELD_TEAM' ? user.id : undefined
    });
    setShowModal(true);
  };

  const handleExportToExcel = async () => {
    try {
      setLoading(true);
      const params: any = {
        search: tableParams.search || undefined,
        enquiry_id: tableParams.enquiry_id,
        user_id: tableParams.user_id,
        rig_id: tableParams.rig_id,
        fromDate: dateRange[0]?.toISOString().split('T')[0],
        toDate: dateRange[1]?.toISOString().split('T')[0]
      };

      const response = await logSheetService.exportToExcel(params);

      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with date range
      const fromDate = dateRange[0] ? dateRange[0].toLocaleDateString('en-GB').replace(/\//g, '-') : 'all';
      const toDate = dateRange[1] ? dateRange[1].toLocaleDateString('en-GB').replace(/\//g, '-') : 'all';
      link.download = `LogSheets_${fromDate}_to_${toDate}.xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast.success('Excel file downloaded successfully');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      showToast.error('Failed to export to Excel');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (logSheet: LogSheet) => {
    setViewingLogSheet(logSheet);
  };

  const handleEdit = (logSheet: LogSheet) => {
    setEditingLogSheet(logSheet);

    // Find and set the enquiry
    if (logSheet.enquiry) {
      setSelectedEnquiry(logSheet.enquiry as any);
    }

    // Calculate prev_hammer_run from hammer_run (reverse calculation)
    // Formula: prev_hammer_run = hammer_run - no_of_feet + pvc_1_feet
    const hammerRun = logSheet.hammer_run || 0;
    const noOfFeet = logSheet.no_of_feet || 0;
    const pvc1Feet = logSheet.pvc_1_feet || 0;
    const calculatedPrevHammerRun = Number((hammerRun - noOfFeet + pvc1Feet).toFixed(2));

    reset({
      enquiry_id: logSheet.enquiry_id || 0,
      log_sheet_no: logSheet.log_sheet_no || '',
      rig_id: logSheet.rig_id || 0,
      log_date: logSheet.log_date ? new Date(logSheet.log_date).toISOString().split('T')[0] : '',
      service_date: logSheet.service_date ? new Date(logSheet.service_date).toISOString().split('T')[0] : '',
      no_of_feet: noOfFeet,
      pvc_1_item_id: logSheet.pvc_1_item_id || 0,
      pvc_1_feet: pvc1Feet,
      pvc_2_item_id: logSheet.pvc_2_item_id || undefined,
      pvc_2_feet: logSheet.pvc_2_feet || undefined,
      bore_well_yield: logSheet.bore_well_yield || undefined,
      rig_no: logSheet.rig_no || '',
      bore_size_id: logSheet.bore_size_id || logSheet.enquiry?.boreSize?.id || 0,
      from_rpm: logSheet.from_rpm || undefined,
      to_rpm: logSheet.to_rpm || undefined,
      from_diesel: logSheet.from_diesel || undefined,
      to_diesel: logSheet.to_diesel || undefined,
      diesel_filled_for_lorry: logSheet.diesel_filled_for_lorry || undefined,
      user_id: logSheet.user_id || undefined,
      prev_hammer_run: calculatedPrevHammerRun,
      hammer_run: hammerRun,
      bit_no: (logSheet as any).bit_no || '',
      bit_depth: (logSheet as any).bit_depth || undefined,
      remarks: logSheet.remarks || ''
    });
    setShowModal(true);
  };

  const handleDelete = (logSheet: LogSheet) => {
    setDeleteConfirm({ show: true, logSheet });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.logSheet) return;
    
    setDeleting(true);
    try {
      await logSheetService.deleteLogSheet(deleteConfirm.logSheet.id);
      showToast.success('Log sheet deleted successfully');
      loadLogSheets(tableParams, sorting);
      setDeleteConfirm({ show: false, logSheet: null });
    } catch (error: any) {
      console.error('Error deleting log sheet:', error);
      showToast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to delete log sheet');
    } finally {
      setDeleting(false);
    }
  };

  // Fetch previous log sheet data for auto-population
  const fetchPreviousLogSheetData = async (rigId: number) => {
    if (!rigId || editingLogSheet) return;

    try {
      // Determine current financial year (Apr 1 – Mar 31)
      const today = new Date();
      const fyStartYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
      const fyFromDate = `${fyStartYear}-04-01`;
      const fyToDate = `${fyStartYear + 1}-03-31`;

      const response = await logSheetService.getLogSheets({
        page: 1,
        limit: 1,
        rig_id: rigId,
        sortBy: 'log_sheet_no',
        sortOrder: 'desc',
        fromDate: fyFromDate,
        toDate: fyToDate
      });

      // Get the latest log sheet for this rig (should be first since we filtered by rig_id)
      const prevLogSheet = response.logSheets[0];

      if (prevLogSheet) {
        // Auto-increment log sheet number
        const prevLogSheetNo = parseInt(prevLogSheet.log_sheet_no || '0');
        setValue('log_sheet_no', String(prevLogSheetNo + 1).padStart(4, '0'));

        // Set From RPM from previous To RPM
        setValue('from_rpm', prevLogSheet.to_rpm || 0);

        // Set Prev Hammer Run
        setValue('prev_hammer_run', prevLogSheet.hammer_run || 0);
      } else {
        // No previous log sheet, set defaults
        setValue('log_sheet_no', '0001');
        setValue('from_rpm', 0);
        setValue('prev_hammer_run', 0);
      }
    } catch (error) {
      console.error('Error fetching previous log sheet:', error);
      // Set defaults on error
      setValue('log_sheet_no', '0001');
      setValue('from_rpm', 0);
      setValue('prev_hammer_run', 0);
    }
  };

  // Handle enquiry selection
  const handleEnquiryChange = (selectedOption: any) => {
    const enquiryId = selectedOption ? parseInt(selectedOption.value) : 0;
    setValue('enquiry_id', enquiryId);

    if (enquiryId) {
      // First try to get enquiry from the async option data
      let enquiry = selectedOption?.enquiry;
      // Fallback to existing enquiries array if not found in option
      if (!enquiry) {
        enquiry = enquiries.find(e => e.id === enquiryId);
      }

      if (enquiry) {
        setSelectedEnquiry(enquiry);
        // Auto-fill fields from enquiry
        if (enquiry.service_date) {
          setValue('service_date', new Date(enquiry.service_date).toISOString().split('T')[0]);
        }
        // Auto-select bore size if available
        if (enquiry.boreSize?.id) {
          setValue('bore_size_id', enquiry.boreSize.id);
        }
        // Auto-select driller (field team) if assigned to enquiry
        if (enquiry.assign_field_team) {
          setValue('user_id', enquiry.assign_field_team);
        }
        // Pre-select rig if available in enquiry
        if (enquiry.rig_id) {
          setValue('rig_id', enquiry.rig_id);
          // Find the rig to set rig_no
          const selectedRig = rigs.find(r => r.id === enquiry.rig_id);
          if (selectedRig) {
            setValue('rig_no', selectedRig.rig_no);
          }
          // Fetch previous log sheet data for this rig
          fetchPreviousLogSheetData(enquiry.rig_id);
        }
      }
    } else {
      setSelectedEnquiry(null);
    }
  };

  // Form submission
  const onSubmit = async (data: LogSheetFormData) => {
    setSubmitting(true);
    try {
      // Clean the data before submission
      const cleanedData = { ...data };

      // If user_id is 0, remove it from the data or set it to undefined
      if (cleanedData.user_id === 0) {
        delete (cleanedData as any).user_id;
      }

      if (editingLogSheet) {
        await logSheetService.updateLogSheet(editingLogSheet.id, cleanedData);
        showToast.success('Log sheet updated successfully');
      } else {
        await logSheetService.createLogSheet(cleanedData);
        showToast.success('Log sheet created successfully');
      }

      setShowModal(false);
      loadLogSheets(tableParams, sorting);
    } catch (error: any) {
      console.error('Error saving log sheet:', error);

      // Handle validation errors from express-validator
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const validationErrors = error.response.data.errors;
        // Show the first validation error message
        const firstError = validationErrors[0];
        showToast.error(firstError.msg || firstError.message || 'Validation failed');
      } else {
        // Handle other error formats
        showToast.error(
          error.response?.data?.error ||
          error.response?.data?.message ||
          (editingLogSheet ? 'Failed to update log sheet' : 'Failed to create log sheet')
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onError = (errors: any) => {
    console.log('Form validation errors:', errors);
    console.log('All error fields:', Object.keys(errors));

    // Manually focus on the first error field in priority order
    setTimeout(() => {
      if (errors.enquiry_id) {
        console.log('Focus: enquiry_id error');
        showToast.error(errors.enquiry_id.message);
      } else if (errors.log_sheet_no) {
        console.log('Focus: log_sheet_no');
        setFocus('log_sheet_no');
        showToast.error(errors.log_sheet_no.message);
      } else if (errors.rig_id) {
        console.log('Focus: rig_id error');
        showToast.error(errors.rig_id.message);
      } else if (errors.log_date) {
        console.log('Focus: log_date');
        setFocus('log_date');
        showToast.error(errors.log_date.message);
      } else if (errors.service_date) {
        console.log('Focus: service_date');
        setFocus('service_date');
        showToast.error(errors.service_date.message);
      } else if (errors.bore_size_id) {
        console.log('Focus: bore_size_id error');
        showToast.error(errors.bore_size_id.message);
      } else if (errors.no_of_feet) {
        console.log('Focus: no_of_feet');
        setFocus('no_of_feet');
        showToast.error(errors.no_of_feet.message);
      }
    }, 100);
  };

  // Table columns
  const columns: ColumnDef<LogSheet>[] = [
    {
      accessorKey: 'log_code',
      header: 'Log Code',
      cell: ({ row }) => (
        <div className="font-medium text-blue-600">
          {row.original.log_code || 'N/A'}
        </div>
      ),
    },
    {
      accessorKey: 'log_sheet_no',
      header: 'Log Sheet No',
      cell: ({ row }) => (
        <div className="font-medium text-gray-900">
          {row.original.log_sheet_no || 'N/A'}
        </div>
      ),
    },
    {
      accessorKey: 'log_date',
      header: 'Date',
      cell: ({ row }) => (
        <div className="text-sm">
          {row.original.log_date ? new Date(row.original.log_date).toLocaleDateString() : 'N/A'}
        </div>
      ),
    },
    {
      accessorKey: 'enquiry',
      header: 'Customer Details',
      cell: ({ row }) => (
        <div className="space-y-1 min-w-0">
          <div className="text-xs text-gray-500">Enq: {row.original.enquiry?.enquiry_no || 'N/A'}</div>
          <div className="font-medium text-gray-900 break-words">{row.original.enquiry?.contact_person || 'N/A'}</div>
          <div className="text-sm text-gray-600 font-mono whitespace-nowrap" title={row.original.enquiry?.contact_no || 'N/A'}>{row.original.enquiry?.contact_no || 'N/A'}</div>
          {row.original.enquiry?.service_date && (
            <div className="text-xs text-gray-500">
              Service: {new Date(row.original.enquiry.service_date).toLocaleDateString()}
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'log_info',
      header: 'Log Info',
      cell: ({ row }) => (
        <div className="space-y-1 text-xs">
          <div><strong>Feet:</strong> {row.original.no_of_feet || 0} ft</div>
          <div><strong>Bore Size:</strong> {row.original.boreSize?.size || row.original.enquiry?.boreSize?.size || 'N/A'}</div>
          <div><strong>PVC 1:</strong> {row.original.pvc1Item?.name || 'N/A'} - {row.original.pvc_1_feet || 0}ft</div>
          {row.original.pvc2Item && <div><strong>PVC 2:</strong> {row.original.pvc2Item.name} - {row.original.pvc_2_feet || 0}ft</div>}
          <div><strong>Yield:</strong> {row.original.bore_well_yield || 0}</div>
        </div>
      ),
    },
    {
      accessorKey: 'user',
      header: 'Field Team',
      cell: ({ row }) => (
        <div className="text-sm">{row.original.user?.name || 'N/A'}</div>
      ),
    },
    {
      accessorKey: 'rig_no',
      header: 'RIG No',
      cell: ({ row }) => (
        <div className="text-sm font-medium">{row.original.rig_no || 'N/A'}</div>
      ),
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleView(row.original)}
            className="h-8 w-8 p-0"
          >
            <EyeIcon className="h-4 w-4" />
          </Button>
          {(user?.role === 'ADMIN' || user?.role === 'FIELD_TEAM') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleEdit(row.original)}
              className="h-8 w-8 p-0"
            >
              <PencilIcon className="h-4 w-4" />
            </Button>
          )}
          {user?.role === 'ADMIN' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(row.original)}
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  // Check for authorization
  if (user && !['ADMIN', 'MANAGER', 'FIELD_TEAM'].includes(user.role)) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
            <p className="text-gray-500">You don't have permission to access this page.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex flex-col h-full gap-4">
        {/* Header */}
        <div className="flex-shrink-0 mb-6">
          {/* Mobile: Compact header with inline actions */}
          <div className="sm:hidden">
            <div className="flex items-center justify-between mb-3">
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold text-gray-900 truncate">Log Sheets</h1>
                {totalRecords > 0 && (
                  <p className="text-xs text-gray-500">
                    {totalRecords} total
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 ml-3">
                <Button
                  onClick={handleExportToExcel}
                  disabled={loading}
                  size="sm"
                  variant="outline"
                  className="p-2"
                  title="Export to Excel"
                >
                  <ArrowDownTrayIcon className="w-5 h-5" />
                </Button>
                <div className="relative">
                  <Button
                    onClick={() => setShowMobileFilters(!showMobileFilters)}
                    size="sm"
                    variant={showMobileFilters ? 'primary' : 'outline'}
                    className="p-2"
                  >
                    {showMobileFilters ? <XMarkIcon className="w-5 h-5" /> : <FunnelIcon className="w-5 h-5" />}
                  </Button>
                  {/* Filter indicator */}
                  {(tableParams.enquiry_id || tableParams.search || dateRange[0] || dateRange[1]) && !showMobileFilters && (
                    <span className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 rounded-full"></span>
                  )}
                </div>
                {(user?.role === 'ADMIN' || user?.role === 'FIELD_TEAM') && (
                  <Button
                    onClick={handleCreate}
                    size="sm"
                    className="p-2"
                  >
                    <PlusIcon className="w-5 h-5" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Desktop: Original layout */}
          <div className="hidden sm:flex sm:justify-between sm:items-center">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Log Sheets</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
                Manage drilling log sheets and operations data
                {totalRecords > 0 && (
                  <span className="ml-2 text-sm text-gray-500">
                    ({totalRecords} total records)
                  </span>
                )}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleExportToExcel}
                disabled={loading}
                size="lg"
                variant="outline"
                leftIcon={<ArrowDownTrayIcon className="w-5 h-5" />}
              >
                Export to Excel
              </Button>
              {(user?.role === 'ADMIN' || user?.role === 'FIELD_TEAM') && (
                <Button
                  onClick={handleCreate}
                  size="lg"
                  leftIcon={<PlusIcon className="w-5 h-5" />}
                >
                  New Log Sheet
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Filters - Always visible */}
        {!isMobile && (
          <div className="flex-shrink-0 mb-6">
            <div className="bg-white p-4 rounded-2xl shadow-medium border border-gray-200">
              <div className="flex gap-4 items-end">
                {/* Search */}
                <div className="w-64">
                  <Input
                    label="Search"
                    placeholder="Search by log code, rig, company, contact, or mobile..."
                    value={tableParams.search}
                    onChange={(e) => setTableParams(prev => ({ ...prev, search: e.target.value, page: 1 }))}
                  />
                </div>

                {/* Date Range */}
                <div className="flex-1 min-w-[320px]">
                  <DateRangePicker
                    label="Date Range"
                    startDate={dateRange[0]}
                    endDate={dateRange[1]}
                    onChange={(dates) => {
                      setDateRange(dates);
                    }}
                    placeholder="Select date range"
                    usePortal={true}
                  />
                </div>

                {/* Enquiry Filter */}
                <div className="w-64">
                  <Select
                    label="Enquiry"
                    placeholder="All Enquiries"
                    value={tableParams.enquiry_id ? {
                      value: tableParams.enquiry_id.toString(),
                      label: enquiries.find(e => e.id === tableParams.enquiry_id)?.enquiry_no || 'Selected'
                    } : null}
                    onChange={(option: any) => setTableParams(prev => ({
                      ...prev,
                      enquiry_id: option ? parseInt(option.value) : undefined,
                      page: 1
                    }))}
                    async
                    asyncConfig={{
                      loadOptions: loadFilterEnquiryOptions,
                      pageSize: 50,
                      debounceTimeout: 300
                    }}
                    isClearable
                    isSearchable
                  />
                </div>

                {/* Rig Filter */}
                <div className="w-64">
                  <Select
                    label="Rig"
                    placeholder="All Rigs"
                    options={rigs.map(rig => ({
                      value: rig.id.toString(),
                      label: rig.rig_no
                    }))}
                    value={tableParams.rig_id ? {
                      value: tableParams.rig_id.toString(),
                      label: rigs.find(r => r.id === tableParams.rig_id)?.rig_no || 'Selected'
                    } : null}
                    onChange={(option: any) => setTableParams(prev => ({
                      ...prev,
                      rig_id: option ? parseInt(option.value) : undefined,
                      page: 1
                    }))}
                    isClearable
                    isSearchable
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Filter Sidebar */}
        <>
          {isMobile && showMobileFilters && (
            <>
              {/* Backdrop overlay */}
              <div
                className="fixed inset-0 bg-black/50 z-40"
                onClick={() => setShowMobileFilters(false)}
              />

              {/* Filter sidebar */}
              <div
                className="fixed top-0 right-0 h-full w-80 bg-white z-50 shadow-xl flex flex-col"
              >
                {/* Sidebar header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                  <button
                    onClick={() => setShowMobileFilters(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                {/* Filter content */}
                <div className="flex-1 p-4 space-y-4 overflow-y-auto">
                  {/* Search */}
                  <Input
                    label="Search"
                    placeholder="Search by log code, rig, company, contact, or mobile..."
                    value={tableParams.search}
                    onChange={(e) => setTableParams(prev => ({ ...prev, search: e.target.value, page: 1 }))}
                  />

                  {/* Date Range */}
                  <DateRangePicker
                    label="Date Range"
                    startDate={dateRange[0]}
                    endDate={dateRange[1]}
                    onChange={(dates) => {
                      setDateRange(dates);
                    }}
                    placeholder="Select date range"
                    usePortal={false}
                  />

                  {/* Enquiry Filter */}
                  <Select
                    label="Enquiry"
                    placeholder="All Enquiries"
                    value={tableParams.enquiry_id ? {
                      value: tableParams.enquiry_id.toString(),
                      label: enquiries.find(e => e.id === tableParams.enquiry_id)?.enquiry_no || 'Selected'
                    } : null}
                    onChange={(option: any) => setTableParams(prev => ({
                      ...prev,
                      enquiry_id: option ? parseInt(option.value) : undefined,
                      page: 1
                    }))}
                    async
                    asyncConfig={{
                      loadOptions: loadFilterEnquiryOptions,
                      pageSize: 50,
                      debounceTimeout: 300
                    }}
                    isClearable
                    isSearchable
                    usePortal={false}
                  />

                  {/* Rig Filter */}
                  <Select
                    label="Rig"
                    placeholder="All Rigs"
                    options={rigs.map(rig => ({
                      value: rig.id.toString(),
                      label: rig.rig_no
                    }))}
                    value={tableParams.rig_id ? {
                      value: tableParams.rig_id.toString(),
                      label: rigs.find(r => r.id === tableParams.rig_id)?.rig_no || 'Selected'
                    } : null}
                    onChange={(option: any) => setTableParams(prev => ({
                      ...prev,
                      rig_id: option ? parseInt(option.value) : undefined,
                      page: 1
                    }))}
                    isClearable
                    isSearchable
                    usePortal={false}
                  />
                </div>

                {/* Sidebar footer with action buttons */}
                <div className="p-4 border-t border-gray-200 space-y-3">
                  <Button
                    onClick={() => {
                      setTableParams(prev => ({
                        ...prev,
                        search: '',
                        enquiry_id: undefined,
                        user_id: undefined,
                        rig_id: undefined,
                        page: 1
                      }));
                      setDateRange(getDefaultDateRange());
                    }}
                    variant="outline"
                    className="w-full"
                  >
                    Clear All Filters
                  </Button>
                  <Button
                    onClick={() => setShowMobileFilters(false)}
                    className="w-full"
                  >
                    Apply Filters
                  </Button>
                </div>
              </div>
            </>
          )}
        </>

        {/* Data Table for Desktop */}
        {!isMobile ? (
          <div className="flex-1 min-h-0 bg-white rounded-2xl shadow-medium border border-gray-200 flex flex-col">
            <DataTable
              columns={columns}
              data={logSheets}
              loading={loading}
              sorting={sorting}
              onSortingChange={setSorting}
              showPagination={false}
              searchable={false}
              pageSize={tableParams.limit}
            />

            {/* Desktop Pagination */}
            <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-t border-gray-200 bg-white">
              <div className="flex items-center justify-between gap-4">
                <div className="text-sm text-gray-700 whitespace-nowrap">
                  Showing {((tableParams.page - 1) * tableParams.limit) + 1} to{' '}
                  {Math.min(tableParams.page * tableParams.limit, totalRecords)} of{' '}
                  {totalRecords} results
                </div>
                <div className="flex items-center gap-4">
                  <Select
                    options={[
                      { value: '5', label: '5 per page' },
                      { value: '10', label: '10 per page' },
                      { value: '25', label: '25 per page' },
                      { value: '50', label: '50 per page' }
                    ]}
                    value={{ value: tableParams.limit.toString(), label: `${tableParams.limit} per page` }}
                    onChange={(option: any) => setTableParams(prev => ({ ...prev, limit: parseInt(option.value), page: 1 }))}
                    usePortal={false}
                    menuPlacement="top"
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setTableParams(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                      disabled={tableParams.page <= 1}
                    >
                      Previous
                    </Button>
                    <div className="flex items-center px-3 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg min-w-[120px] justify-center">
                      Page {tableParams.page} of {totalPages}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setTableParams(prev => ({ ...prev, page: Math.min(totalPages, prev.page + 1) }))}
                      disabled={tableParams.page >= totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Mobile Cards View
          <div className="flex-1 min-h-0 flex flex-col gap-4 relative">
            {loading && (
              <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10 rounded-2xl">
                <div className="flex flex-col items-center p-6 bg-white rounded-2xl shadow-strong border border-gray-200">
                  <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                  <p className="text-gray-600 text-sm font-medium mt-3">Loading log sheets...</p>
                </div>
              </div>
            )}
            <div className="flex-1 min-h-0 overflow-auto bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="divide-y divide-gray-100">
                {logSheets.map((logSheet, index) => (
                  <div
                    key={logSheet.id}
                    className="bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors"
                  >
                    <div className="px-4 py-3">
                      <div className="flex items-start space-x-3">
                        {/* Compact number indicator */}
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0 mt-0.5">
                          {((tableParams.page - 1) * tableParams.limit) + index + 1}
                        </div>

                        <div className="flex-1 min-w-0">
                          {/* Main content */}
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              {/* Primary info */}
                              <div className="font-medium text-gray-900 truncate">
                                {logSheet.log_code}
                              </div>

                              {/* Secondary info line */}
                              <div className="flex items-center space-x-2 text-xs text-gray-500 mt-0.5">
                                <span className="truncate">
                                  {logSheet.enquiry?.contact_person || 'N/A'}
                                </span>
                                {(logSheet.boreSize || logSheet.enquiry?.boreSize) && (
                                  <>
                                    <span>•</span>
                                    <span>{logSheet.boreSize?.size || logSheet.enquiry?.boreSize?.size}</span>
                                  </>
                                )}
                              </div>

                              {/* Date, RIG, and Feet row */}
                              <div className="flex items-center space-x-3 mt-1">
                                {logSheet.log_date && (
                                  <span className="text-xs text-gray-600">
                                    {new Date(logSheet.log_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  </span>
                                )}
                                {logSheet.rig_no && (
                                  <>
                                    {logSheet.log_date && <span className="text-gray-300">•</span>}
                                    <span className="text-xs text-gray-600">
                                      RIG: {logSheet.rig_no}
                                    </span>
                                  </>
                                )}
                                {logSheet.no_of_feet !== null && logSheet.no_of_feet !== undefined && (
                                  <>
                                    {(logSheet.log_date || logSheet.rig_no) && <span className="text-gray-300">•</span>}
                                    <span className="text-xs text-gray-600">
                                      {logSheet.no_of_feet} ft
                                    </span>
                                  </>
                                )}
                              </div>

                              {/* Team info */}
                              {logSheet.user && (
                                <div className="flex items-center gap-2 mt-2">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">
                                    {logSheet.user.name}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Action menu */}
                            <div className="relative ml-3">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowActionMenu(showActionMenu === logSheet.id ? null : logSheet.id);
                                }}
                                className="p-1 hover:bg-gray-200 rounded transition-colors"
                              >
                                <EllipsisVerticalIcon className="w-4 h-4 text-gray-400" />
                              </button>

                              {showActionMenu === logSheet.id && (
                                <div className="absolute right-0 mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                                  <button
                                    onClick={() => {
                                      setViewingLogSheet(logSheet);
                                      setShowActionMenu(null);
                                    }}
                                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                                  >
                                    <EyeIcon className="w-3 h-3 mr-2" />
                                    View
                                  </button>
                                  {(user?.role === 'ADMIN' || user?.role === 'FIELD_TEAM') && (
                                    <button
                                      onClick={() => {
                                        handleEdit(logSheet);
                                        setShowActionMenu(null);
                                      }}
                                      className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                                    >
                                      <PencilIcon className="w-3 h-3 mr-2" />
                                      Edit
                                    </button>
                                  )}
                                  {user?.role === 'ADMIN' && (
                                    <button
                                      onClick={() => {
                                        handleDelete(logSheet);
                                        setShowActionMenu(null);
                                      }}
                                      className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                                    >
                                      <TrashIcon className="w-3 h-3 mr-2" />
                                      Delete
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {logSheets.length === 0 && !loading && (
                  <div className="text-center py-12">
                    <div className="text-gray-500 text-lg">No log sheets found</div>
                    <div className="text-gray-400 text-sm mt-2">Try adjusting your search or filter criteria</div>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Pagination */}
            <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 bg-white rounded-b-2xl">
              <div className="flex items-center justify-between gap-2">
                {/* Per page selector on left */}
                <div className="w-20">
                  <Select
                    options={[
                      { value: '5', label: '5' },
                      { value: '10', label: '10' },
                      { value: '25', label: '25' },
                      { value: '50', label: '50' }
                    ]}
                    value={{ value: tableParams.limit.toString(), label: tableParams.limit.toString() }}
                    onChange={(option: any) => setTableParams(prev => ({ ...prev, limit: parseInt(option.value), page: 1 }))}
                    usePortal={false}
                    menuPlacement="top"
                    isSearchable={false}
                  />
                </div>

                {/* Navigation on right */}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setTableParams(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={tableParams.page <= 1}
                    className="px-3 h-8"
                  >
                    ←
                  </Button>

                  <div className="text-xs text-gray-600 bg-gray-100 px-3 py-1 rounded-full whitespace-nowrap">
                    Page {tableParams.page} of {totalPages}
                  </div>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setTableParams(prev => ({ ...prev, page: Math.min(totalPages, prev.page + 1) }))}
                    disabled={tableParams.page >= totalPages}
                    className="px-3 h-8"
                  >
                    →
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Modal */}
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={editingLogSheet ? 'Edit Log Sheet' : 'Create New Log Sheet'}
          size="xl"
          footer={
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="logsheet-form"
                loading={submitting}
                disabled={!selectedEnquiry && !editingLogSheet}
              >
                {editingLogSheet ? 'Update' : 'Create'} Log Sheet
              </Button>
            </>
          }
        >
          <form id="logsheet-form" onSubmit={handleSubmit(onSubmit, onError)} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700">Basic Information</h3>
                
                {editingLogSheet ? (
                  // Readonly enquiry field when editing
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Enquiry (Read-only)</label>
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                      {selectedEnquiry ? 
                        `${selectedEnquiry.enquiry_no || 'N/A'} - ${selectedEnquiry.contact_person || 'N/A'} (${selectedEnquiry.contact_no || 'N/A'})` : 
                        'N/A'
                      }
                    </div>
                  </div>
                ) : (
                  // Dropdown for new log sheets (only unmapped enquiries)
                  <Select
                    label="Select Enquiry *"
                    value={selectedEnquiry ? {
                      value: selectedEnquiry.id.toString(),
                      label: `${selectedEnquiry.enquiry_no || 'N/A'} - ${selectedEnquiry.contact_person || 'N/A'} (${selectedEnquiry.contact_no || 'N/A'})`
                    } : null}
                    onChange={handleEnquiryChange}
                    async
                    asyncConfig={{
                      loadOptions: loadEnquiryOptions,
                      pageSize: 50,
                      debounceTimeout: 300
                    }}
                    error={errors.enquiry_id?.message}
                    isSearchable
                    isClearable
                    placeholder="Search unmapped enquiries..."
                  />
                )}

                {selectedEnquiry && (
                  <div className="bg-gray-50 p-3 rounded border">
                    <div className="text-xs space-y-1">
                      <div><strong>Enquiry:</strong> {selectedEnquiry.enquiry_no}</div>
                      <div><strong>Contact:</strong> {selectedEnquiry.contact_person} ({selectedEnquiry.contact_no})</div>
                      <div><strong>Bore Size:</strong> {selectedEnquiry.boreSize?.size}</div>
                    </div>
                  </div>
                )}

                <Input
                  label="Log Sheet No *"
                  type="text"
                  placeholder="Enter 4-6 digit number"
                  maxLength={6}
                  {...register('log_sheet_no')}
                  error={errors.log_sheet_no?.message}
                  onChange={(e) => {
                    // Only allow digits
                    const value = e.target.value.replace(/\D/g, '');
                    setValue('log_sheet_no', value);
                  }}
                />

                <Input
                  label="Log Date *"
                  type="date"
                  {...register('log_date')}
                  error={errors.log_date?.message}
                />

                <Input
                  label="Service Date *"
                  type="date"
                  {...register('service_date')}
                  error={errors.service_date?.message}
                />

                <Select
                  label="Bore Size *"
                  options={boreSizeOptions}
                  value={watch('bore_size_id')?.toString() || ''}
                  onChange={(selectedOption: any) => {
                    setValue('bore_size_id', selectedOption ? parseInt(selectedOption.value) : 0);
                  }}
                  error={errors.bore_size_id?.message}
                  isSearchable
                  isClearable
                />

                <Input
                  label="No of Feet *"
                  type="number"
                  step="0.01"
                  {...register('no_of_feet', { valueAsNumber: true })}
                  error={errors.no_of_feet?.message}
                />

                {/* PVC & Yield Details moved from middle column */}
                <div className="space-y-3 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700">PVC & Yield Details</h4>

                  <div className="space-y-3">
                    <div className="text-xs font-medium text-gray-600">PVC 1</div>
                    <Select
                      label="PVC Item 1"
                      options={pvcItems.filter(item => item.type === 'PVC').map(item => ({
                        value: item.id.toString(),
                        label: `${item.name} (₹${item.rate})`
                      }))}
                      value={watch('pvc_1_item_id')?.toString() || ''}
                      onChange={(selectedOption: any) => {
                        setValue('pvc_1_item_id', selectedOption ? parseInt(selectedOption.value) : 0);
                      }}
                      error={errors.pvc_1_item_id?.message}
                      isSearchable
                      isClearable
                    />
                    <Input
                      label="PVC 1 Feet"
                      type="number"
                      step="0.01"
                      placeholder="Enter feet"
                      {...register('pvc_1_feet', { valueAsNumber: true })}
                      error={errors.pvc_1_feet?.message}
                    />
                  </div>

                  <div className="space-y-3">
                    <div className="text-xs font-medium text-gray-600">PVC 2 - Optional</div>
                    <Select
                      label="PVC Item 2"
                      options={pvcItems.filter(item => item.type === 'PVC').map(item => ({
                        value: item.id.toString(),
                        label: `${item.name} (₹${item.rate})`
                      }))}
                      value={watch('pvc_2_item_id')?.toString() || ''}
                      onChange={(selectedOption: any) => {
                        setValue('pvc_2_item_id', selectedOption ? parseInt(selectedOption.value) : undefined);
                      }}
                      error={errors.pvc_2_item_id?.message}
                      isSearchable
                      isClearable
                    />
                    <Input
                      label="PVC 2 Feet"
                      type="number"
                      step="0.01"
                      placeholder="Enter feet"
                      {...register('pvc_2_feet', { valueAsNumber: true })}
                      error={errors.pvc_2_feet?.message}
                    />
                  </div>

                  <Input
                    label="Borewell Yield"
                    type="text"
                    placeholder="Enter yield (number or text)"
                    {...register('bore_well_yield')}
                    error={errors.bore_well_yield?.message}
                  />
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700">Operation Details</h3>

                <Select
                  label="Rig *"
                  options={rigs.map(rig => ({
                    value: rig.id.toString(),
                    label: rig.rig_no
                  }))}
                  value={watch('rig_id') ? {
                    value: watch('rig_id').toString(),
                    label: rigs.find(r => r.id === watch('rig_id'))?.rig_no || ''
                  } : null}
                  onChange={async (selectedOption: any) => {
                    const rigId = selectedOption ? parseInt(selectedOption.value) : 0;
                    setValue('rig_id', rigId);
                    // Also set rig_no for backward compatibility
                    const selectedRig = rigs.find(r => r.id === rigId);
                    setValue('rig_no', selectedRig ? selectedRig.rig_no : '');

                    // Fetch previous log sheet data for this rig
                    await fetchPreviousLogSheetData(rigId);
                  }}
                  error={errors.rig_id?.message}
                  isSearchable
                  isClearable
                  placeholder="Select rig..."
                />

                <div className="space-y-2">
                  <div className="text-xs font-medium text-gray-600">RPM Range</div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      label="To RPM"
                      placeholder="Ending RPM"
                      type="number"
                      step="0.01"
                      {...register('to_rpm', { valueAsNumber: true })}
                      error={errors.to_rpm?.message}
                    />
                    <Input
                      label="From RPM"
                      placeholder="Starting RPM"
                      type="number"
                      step="0.01"
                      {...register('from_rpm', { valueAsNumber: true })}
                      error={errors.from_rpm?.message}
                    />
                    <Input
                      label="RPM"
                      placeholder="Auto calculated"
                      type="number"
                      value={(() => {
                        const toRpm = watch('to_rpm');
                        const fromRpm = watch('from_rpm');
                        if (toRpm && fromRpm) {
                          return Number((toRpm - fromRpm).toFixed(2));
                        }
                        return '';
                      })()}
                      disabled
                      className="bg-gray-100"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-xs font-medium text-gray-600">Diesel Consumption</div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      label="From Diesel"
                      placeholder="Starting level"
                      type="number"
                      step="0.01"
                      {...register('from_diesel', { valueAsNumber: true })}
                      error={errors.from_diesel?.message}
                    />
                    <Input
                      label="To Diesel"
                      placeholder="Ending level"
                      type="number"
                      step="0.01"
                      {...register('to_diesel', { valueAsNumber: true })}
                      error={errors.to_diesel?.message}
                    />
                  </div>
                </div>

                {(user?.role === 'ADMIN' || user?.role === 'MANAGER') && (
                  <Select
                    label="Driller (Field Team)"
                    value={fieldTeamOptions.find(option => parseInt(option.value) === watch('user_id')) || null}
                    onChange={(option: any) => setValue('user_id', option ? parseInt(option.value) : 0)}
                    options={fieldTeamOptions}
                    error={errors.user_id?.message}
                    isSearchable
                  />
                )}

                {/* Prev Hammer Run and Hammer Run in one row */}
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Prev Hammer Run"
                    type="number"
                    step="0.01"
                    {...register('prev_hammer_run', { valueAsNumber: true })}
                    error={errors.prev_hammer_run?.message}
                    readOnly
                    className="bg-gray-100"
                  />
                  <Input
                    label="Hammer Run"
                    type="number"
                    step="0.01"
                    placeholder={(() => {
                      const prevHammerRun = watch('prev_hammer_run') || 0;
                      const noOfFeet = watch('no_of_feet') || 0;
                      const pvc1Feet = watch('pvc_1_feet') || 0;
                      return `Suggested: ${Number((prevHammerRun + noOfFeet - pvc1Feet).toFixed(2))}`;
                    })()}
                    {...register('hammer_run', { valueAsNumber: true })}
                    error={errors.hammer_run?.message}
                  />
                </div>


                {/* New fields: Bit No and Bit Depth */}
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Bit No"
                    type="text"
                    maxLength={50}
                    {...register('bit_no')}
                    error={errors.bit_no?.message}
                    placeholder="Enter bit number..."
                  />
                  <Input
                    label="Bit Depth"
                    type="number"
                    step="0.01"
                    {...register('bit_depth', { valueAsNumber: true })}
                    error={errors.bit_depth?.message}
                    placeholder="Enter bit depth..."
                  />
                </div>

                <div className="space-y-2">
                  <Input
                    label="Diesel Filled for Lorry"
                    placeholder="Enter diesel quantity"
                    type="number"
                    step="0.01"
                    {...register('diesel_filled_for_lorry', { valueAsNumber: true })}
                    error={errors.diesel_filled_for_lorry?.message}
                  />
                </div>

                {/* Pipeline Section */}
                <div className="space-y-3 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700">Pipeline Details</h4>
                  <div className="space-y-3">
                    <div className="text-xs font-medium text-gray-600">Pipeline - Optional</div>
                    <Select
                      label="Pipeline Item"
                      options={pvcItems.filter(item => item.type === 'PIPELINE').map(item => ({
                        value: item.id.toString(),
                        label: `${item.name} (₹${item.rate})`
                      }))}
                      value={watch('pipeline_item_id')?.toString() || ''}
                      onChange={(selectedOption: any) => {
                        setValue('pipeline_item_id', selectedOption ? parseInt(selectedOption.value) : undefined);
                      }}
                      error={errors.pipeline_item_id?.message}
                      isSearchable
                      isClearable
                    />
                    <Input
                      label="Pipeline Feet"
                      type="number"
                      step="0.01"
                      placeholder="Enter feet"
                      {...register('pipeline_feet', { valueAsNumber: true })}
                      error={errors.pipeline_feet?.message}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Remarks */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remarks
              </label>
              <textarea
                {...register('remarks')}
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="Enter remarks..."
              />
              {errors.remarks && (
                <p className="mt-1 text-sm text-red-600">{errors.remarks.message}</p>
              )}
            </div>
          </form>
        </Modal>

        {/* View Modal */}
        {viewingLogSheet && (
          <Modal
            isOpen={!!viewingLogSheet}
            onClose={() => setViewingLogSheet(null)}
            title="Log Sheet Details"
            footer={
              <Button
                type="button"
                onClick={() => setViewingLogSheet(null)}
              >
                Close
              </Button>
            }
          >
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Basic Information</h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  <div>
                    <span className="text-gray-500">Log Code:</span>
                    <div className="font-medium text-gray-900">{viewingLogSheet.log_code || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">RIG No:</span>
                    <div className="font-medium text-gray-900">{viewingLogSheet.rig_no || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Log Date:</span>
                    <div className="font-medium text-gray-900">
                      {viewingLogSheet.log_date ? new Date(viewingLogSheet.log_date).toLocaleDateString() : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">Service Date:</span>
                    <div className="font-medium text-gray-900">
                      {viewingLogSheet.service_date ? new Date(viewingLogSheet.service_date).toLocaleDateString() : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">No of Feet:</span>
                    <div className="font-medium text-gray-900">{viewingLogSheet.no_of_feet || 0} ft</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Bore Size:</span>
                    <div className="font-medium text-gray-900">{viewingLogSheet.boreSize?.size || viewingLogSheet.enquiry?.boreSize?.size || 'N/A'}</div>
                  </div>
                </div>
              </div>

              {/* Enquiry Information */}
              {viewingLogSheet.enquiry && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Enquiry Information</h3>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                    <div>
                      <span className="text-gray-500">Enquiry No:</span>
                      <div className="font-medium text-gray-900">{viewingLogSheet.enquiry.enquiry_no || 'N/A'}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Company:</span>
                      <div className="font-medium text-gray-900">{viewingLogSheet.enquiry.company_name || 'N/A'}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Contact Person:</span>
                      <div className="font-medium text-gray-900">{viewingLogSheet.enquiry.contact_person || 'N/A'}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Contact No:</span>
                      <div className="font-medium text-gray-900">{viewingLogSheet.enquiry.contact_no || 'N/A'}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* PVC & Pipeline Details */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">PVC & Pipeline Details</h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  <div className="col-span-2">
                    <span className="text-gray-500 font-medium">PVC 1:</span>
                    <div className="font-medium text-gray-900 mt-1">
                      {viewingLogSheet.pvc1Item?.name || 'N/A'} - {viewingLogSheet.pvc_1_feet || 0} ft
                    </div>
                  </div>
                  {viewingLogSheet.pvc2Item && (
                    <div className="col-span-2">
                      <span className="text-gray-500 font-medium">PVC 2:</span>
                      <div className="font-medium text-gray-900 mt-1">
                        {viewingLogSheet.pvc2Item.name} - {viewingLogSheet.pvc_2_feet || 0} ft
                      </div>
                    </div>
                  )}
                  {viewingLogSheet.pipelineItem && (
                    <div className="col-span-2">
                      <span className="text-gray-500 font-medium">Pipeline:</span>
                      <div className="font-medium text-gray-900 mt-1">
                        {viewingLogSheet.pipelineItem.name} - {viewingLogSheet.pipeline_feet || 0} ft
                      </div>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">Borewell Yield:</span>
                    <div className="font-medium text-gray-900">{viewingLogSheet.bore_well_yield || 0}</div>
                  </div>
                </div>
              </div>

              {/* RPM & Diesel */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">RPM & Diesel</h3>
                <div className="grid grid-cols-3 gap-x-4 gap-y-3 text-sm">
                  <div>
                    <span className="text-gray-500">To RPM:</span>
                    <div className="font-medium text-gray-900">{viewingLogSheet.to_rpm || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">From RPM:</span>
                    <div className="font-medium text-gray-900">{viewingLogSheet.from_rpm || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">RPM:</span>
                    <div className="font-medium text-gray-900">
                      {viewingLogSheet.to_rpm && viewingLogSheet.from_rpm
                        ? (viewingLogSheet.to_rpm - viewingLogSheet.from_rpm).toFixed(2)
                        : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">From Diesel:</span>
                    <div className="font-medium text-gray-900">{viewingLogSheet.from_diesel || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">To Diesel:</span>
                    <div className="font-medium text-gray-900">{viewingLogSheet.to_diesel || 'N/A'}</div>
                  </div>
                </div>
              </div>

              {/* Technical Details */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Technical Details</h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  <div>
                    <span className="text-gray-500">Prev Hammer Run:</span>
                    <div className="font-medium text-gray-900">{viewingLogSheet.prev_hammer_run || 0}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Hammer Run:</span>
                    <div className="font-medium text-gray-900">{viewingLogSheet.hammer_run || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Bit No:</span>
                    <div className="font-medium text-gray-900">{viewingLogSheet.bit_no || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Bit Depth:</span>
                    <div className="font-medium text-gray-900">{viewingLogSheet.bit_depth || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Diesel Filled for Lorry:</span>
                    <div className="font-medium text-gray-900">{viewingLogSheet.diesel_filled_for_lorry || 'N/A'}</div>
                  </div>
                </div>
              </div>

              {/* Team & Status */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Team & Status</h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  {viewingLogSheet.user && (
                    <div>
                      <span className="text-gray-500">Field Team:</span>
                      <div className="font-medium text-gray-900">{viewingLogSheet.user.name}</div>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">Is Billed:</span>
                    <div className="font-medium text-gray-900">{viewingLogSheet.is_billed || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Is Cancelled:</span>
                    <div className="font-medium text-gray-900">{viewingLogSheet.is_cancelled || 'N/A'}</div>
                  </div>
                  {viewingLogSheet.createdByUser && (
                    <div>
                      <span className="text-gray-500">Created By:</span>
                      <div className="font-medium text-gray-900">{viewingLogSheet.createdByUser.name}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Remarks */}
              {viewingLogSheet.remarks && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Remarks</h3>
                  <div className="text-sm text-gray-900">{viewingLogSheet.remarks}</div>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <Button variant="outline" onClick={() => setViewingLogSheet(null)}>
                Close
              </Button>
            </div>
          </Modal>
        )}

        {/* Delete Confirmation */}
        <ConfirmDialog
          isOpen={deleteConfirm.show}
          onClose={() => setDeleteConfirm({ show: false, logSheet: null })}
          onConfirm={confirmDelete}
          title="Delete Log Sheet"
          message={`Are you sure you want to delete this log sheet? This action cannot be undone.`}
          confirmText="Delete"
          loading={deleting}
          variant="danger"
        />
      </div>
    </MainLayout>
  );
};

export default LogSheetManagement;